// @noflow
import { Actions as FarceActions } from 'farce';
import { isEmpty } from 'lodash';
import cookie from 'react-cookies';
import { Observable } from 'rxjs/Observable';
import deviceType from 'Common/app/getDeviceType';
import type { Action, SamlAccountInfo, DishSession, RadishSessionParams } from './types';
import type { Dispatch, GetState, ThunkAction } from '../types';
import isReactNative from '../app/isReactNative';
import { fetchUserTimerPreferences } from '../preferences/actions';
import { receiverCanRecord } from '../app/actions';
import { postJson, getJson, appendParamsToQueryString, paramify } from '../utilities/fetchUtils';
import { parseRadishSetCookie } from './utils';
import { platform } from '../player/utils';
import isClient from '../app/isClient';
import { fetchActiveProfile, fetchProfiles } from '../profiles/actions';
import { fetchRecordings, fetchRecordingsDetails, fetchTimers } from '../dvr/actions';
import { fetchPlayerCustomAttributes } from '../player/actions';

export const signOut = ({
  isJsInitiated,
  isForceLogout,
  forceLogoutCode,
  httpErrorCode,
  error,
}: {
  isJsInitiated: boolean,
  isForceLogout: boolean,
  forceLogoutCode: number,
  httpErrorCode: number,
  error?: Object,
}): Action => ({
  type: 'SIGN_OUT',
  isJsInitiated,
  isForceLogout,
  forceLogoutCode,
  httpErrorCode,
  error,
});

const SESSION_REQUEST_TIMEOUT = 20000; // 20 seconds

const signInDone = (user: DishSession): ThunkAction => ({ dispatch }) => {
  dispatch({
    type: 'SIGN_IN_DONE',
    payload: { user },
  });

  if (isClient && !process.env.IN_CHROMECAST_MODE) {
    /* Following request is not necessary for SSR  */
    dispatch(fetchUserTimerPreferences());
    dispatch(fetchProfiles());
  }
};

export const createRadishSession = (rs: string, expires: number, created: number = undefined) => ({
  dispatch,
}) => {
  dispatch({
    type: 'CREATE_RADISH_SESSION',
    payload: {
      rs,
      expires,
      created,
    },
  });
};

const processDishSession = (dishSession: DishSession, getState: GetState, dispatch: Dispatch) => {
  if (!dishSession) return Promise.reject();

  let nickname = '';
  let finderId = '';
  let slingAdmin = '';
  let model = '';
  let status = '';
  let canRecord = '';
  let zipCode = '';
  let stbSoftwareVersion = '';
  let stbUtcOffset = '';

  // eslint-disable-next-line prefer-const
  let { last_receiver_id: lastReceiverId, receivers, other_hardware: otherHardware } = dishSession;

  if (!isEmpty(receivers)) {
    let lastReceiver;

    if (lastReceiverId) {
      lastReceiver = receivers.find(
        receiver =>
          receiver.sge_receiver_id === lastReceiverId || receiver.receiver_id === lastReceiverId,
      );
    }

    if (!lastReceiver) {
      [lastReceiver] = receivers;
      lastReceiverId = lastReceiver.receiver_id;
    }

    ({
      nickname,
      finder_id: finderId,
      sling_admin_pw: slingAdmin,
      model,
      state: status,
      hdd_capable: canRecord,
      zip_code: zipCode,
      stb_software_version: stbSoftwareVersion,
      local_offset: stbUtcOffset,
    } = lastReceiver);
  }

  dishSession.joeys = otherHardware;

  dispatch(signInDone(dishSession));

  /* NOTE: Chromecast receiver inherits active receiver from the sender. */
  if (process.env.IN_CHROMECAST_MODE) return undefined;

  // const {
  //   app: { activeReceiver: { receiverId: cachedLastReceiverId } = {} },
  // } = getState();

  if (lastReceiverId) {
    lastReceiverId = lastReceiverId.replace(/dishR?/, 'R');
    dispatch({
      type: 'CHANGE_ACTIVE_RECEIVER_SUCCESS',
      receiverId: lastReceiverId,
      nickname,
      model,
      state: status,
      finderId: typeof finderId === 'string' ? finderId.replace(/[.]/g, '') : '',
      slingAdmin,
      canRecord: receiverCanRecord(model),
      zipCode,
      stbSoftwareVersion,
      stbUtcOffset,
    });
  }

  if (isClient) {
    dispatch(fetchTimers());
    if (deviceType !== 'Tv') {
      dispatch(fetchRecordings());
      dispatch(fetchRecordingsDetails());
    }
  }
  dispatch(fetchPlayerCustomAttributes());

  return undefined;
};

export const getRadishSession = (params: RadishSessionParams = {}): ThunkAction => ({
  getState,
  dispatch,
}): Promise<any> => {
  const state = getState();
  const {
    auth: { viewer: { uuid, samlToken = '' } = {}, isFromPairing },
    networkInfo,
  } = state;

  let {
    auth: { radishSession: { rs, created, expires } = {} },
  } = state;

  if (!uuid) {
    return Promise.resolve();
  }

  if (!isReactNative) {
    rs = cookie.load('rs_redux');
    created = cookie.load('rs_created');
    expires = cookie.load('rs_expires');
  }

  const clientIp = networkInfo.externalPublicIp || networkInfo.details?.ipAddress;
  const { isCacheLogin = true } = params;
  const appendDeviceType = url =>
    appendParamsToQueryString(
      url,
      paramify({
        device_type: encodeURIComponent(`${deviceType}-${platform}`),
      }),
    );
  const appendClientIp = url =>
    appendParamsToQueryString(
      url,
      paramify({
        client_ip: clientIp ? encodeURIComponent(clientIp) : undefined,
      }),
      true,
    );
  const appendRadishDebug = url =>
    appendParamsToQueryString(
      url,
      paramify(
        {
          rs_redux: rs,
          rs_created: created,
          rs_expires: expires,
        },
        true,
      ),
    );

  const sessionRequest = isCacheLogin
    ? getJson({
        // All cached logins for Web and Native, just to get session info
        url: appendRadishDebug(
          appendClientIp(appendDeviceType(`${state.config.session.radish_url}session_info`)),
        ),
        state,
        headers: { Accept: 'application/vnd.echostar.session+json;version=1' },
        withResponseHeaders: true,
      })
    : // isFromPairing should be enough on its own, but there is analytics data showing,
    // that secure session requests are made with fake Synacor tokens. Must be some bug...
    isFromPairing || samlToken.startsWith('hp_') || process.env.IN_CHROMECAST_MODE
    ? postJson({
        // Native pairing and Chromecast Receiver
        // This end point is going to be deprecated and clientIp needs to be sent to Radish via the client server
        url: appendClientIp(
          `${state.config.session.radish_url}v20/dol/users/${uuid}/dish_session.json`,
        ),
        state,
        requestTimeoutMs: process.env.IN_CHROMECAST_MODE ? undefined : SESSION_REQUEST_TIMEOUT,
        withResponseHeaders: true,
      })
    : postJson({
        // Native clients fresh login
        url: `${state.config.session.radish_url}session`,
        data: `token=${samlToken}&uuid=${uuid}&device_type=${`${deviceType}-${platform}`}${
          clientIp ? `&client_ip=${clientIp}` : ''
        }`,
        state,
        headers: { Accept: 'application/vnd.echostar.session+json;version=1' },
        requestTimeoutMs: SESSION_REQUEST_TIMEOUT,
        withResponseHeaders: true,
      });

  return sessionRequest
    .then(response => {
      // Left for dev testing login timeouts.
      // const {
      //   auth: { signInRetryCount = 0 },
      // } = state;
      // // ******** set to '< 3' here \/ to see "Request timed out"
      // if (!isCacheLogin && signInRetryCount < 2) {
      //   const error = { type: 'Timeout' };
      //   return new Promise((_, reject) => {
      //     setTimeout(() => {
      //       reject(error);
      //     }, 5000);
      //   });
      // }
      // Left for dev testing login errors.
      // if (isCacheLogin) {
      //   const error = { httpErrorCode: 401 };
      //   throw error;
      // }
      const { response: dishSession, headers } = response;

      if (isReactNative && !isCacheLogin && headers?.map) {
        const rsCookie = parseRadishSetCookie(headers.map['set-cookie']);
        if (rsCookie?.value) {
          dispatch(createRadishSession(rsCookie.value, Date.parse(rsCookie?.expires || 0)));
        }
      }

      const {
        auth: { viewer: { uuid: currentUuid } = {} },
      } = getState();
      if (currentUuid !== uuid) {
        // Android: we might have picked another user or signed out
        // and this is a dangling session request.
        // Discard it.
        // This ugly situation may happen because JS and Java handle cached login
        // in parallel and asynchronously. When we switch a user in native due to SSO
        // business logic or change paired user to one from SSO, JS still has the old
        // UUID and runs a dish_session. Native might have sent a SIGN_OUT and maybe
        // already a new user with ON_AUTH between dish_session request and response.
        return;
      }
      processDishSession(dishSession, getState, dispatch);
      /* NOTE: it is required to load active profile on server side (for WEB),
       because some functionality (like kids mode, watchlist) rely on it. */
      if (isClient) {
        dispatch(fetchActiveProfile());
      }
    })
    .catch(error => {
      if (error && isReactNative) {
        // This means our session request has failed.
        // We are forced to log out.
        const { headers, httpErrorCode, status } = error;
        const forceLogoutCode = headers?.get('force-logout');

        // 400: client error, there were missing parameters or not https
        // 401: unauthorized, the session passed in was not valid
        // 402: synacor rejected the token passed in
        // 403: forbidden, dish api or other upstream provider rejected the user or failed
        // 451: zeus failed to provide an enterprise token, which I guess still matters but it feels like it would be better to burn books
        const code = httpErrorCode || status;
        const timeOutCode = error.type === 'Timeout' ? 999 : undefined;

        const isForceLogout =
          code === 400 ||
          code === 401 ||
          code === 402 ||
          code === 403 ||
          code === 451 ||
          forceLogoutCode === '1' ||
          forceLogoutCode === '2' ||
          forceLogoutCode === '3';

        setTimeout(() => {
          dispatch(
            signOut({
              isForceLogout,
              forceLogoutCode,
              httpErrorCode: code || timeOutCode,
              error,
            }),
          );
        }, 2000); // Use setTimeout for codepush timing
      }
    });
};

export const onAuth = (
  accountInfo: SamlAccountInfo,
  isFromPairing: boolean = false,
  androidSsoAccountOwner?: string,
): ThunkAction => ({ dispatch }) => {
  dispatch({
    type: 'ON_AUTH',
    payload: { accountInfo, isFromPairing, androidSsoAccountOwner },
  });
  dispatch(getRadishSession({ isCacheLogin: false }));
};

export const onIosNativeLogin = (loginType: string, isLoggingIn: boolean) => ({
  type: 'ON_IOS_NATIVE_LOGIN',
  loginType,
  isLoggingIn,
});

export const onAuthError = (): Action => ({ type: 'ON_AUTH_ERROR' });

const signOutEpic = (action$: any) =>
  action$
    .filter((action: Action) => action.type === 'SIGN_OUT')
    .mergeMap(() =>
      // TODO sign out
      isReactNative ? Observable.of() : Observable.of(FarceActions.push('/')),
    );

export const epics = process.env.IN_CHROMECAST_MODE ? [] : [signOutEpic];
